import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { 
  Search, 
  Trash2, 
  Shield, 
  UserCog, 
  Key, 
  Loader2,
  Users,
  Building,
  RefreshCw
} from 'lucide-react';

export default function UserManagement({ customers = [] }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Dialog states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  
  // Form states
  const [newPassword, setNewPassword] = useState('');
  const [assignCustomerId, setAssignCustomerId] = useState('');
  const [assignRole, setAssignRole] = useState('admin');

  // Search users
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    setLoading(true);
    try {
      const results = await api.searchUsers(searchQuery);
      setUsers(results);
      if (results.length === 0) {
        toast.info('No users found');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Load all users
  const loadAllUsers = async () => {
    setLoading(true);
    try {
      const results = await api.getAllUsers();
      setUsers(results);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  // Delete user
  const handleDelete = async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      await api.adminDeleteUser(selectedUser.id);
      toast.success(`User ${selectedUser.email} deleted`);
      setDeleteDialogOpen(false);
      setUsers(users.filter(u => u.id !== selectedUser.id));
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setLoading(false);
    }
  };

  // Promote to Super Admin
  const handlePromote = async () => {
    if (!selectedUser) return;
    
    setLoading(true);
    try {
      await api.promoteToSuperAdmin(selectedUser.email);
      toast.success(`${selectedUser.email} promoted to Super Admin`);
      setPromoteDialogOpen(false);
      // Refresh user in list
      setUsers(users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, role: 'super_admin', customer_id: null }
          : u
      ));
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Promotion failed');
    } finally {
      setLoading(false);
    }
  };

  // Assign to Customer
  const handleAssign = async () => {
    if (!selectedUser || !assignCustomerId) {
      toast.error('Please select a customer');
      return;
    }
    
    setLoading(true);
    try {
      await api.assignUserToCustomer(selectedUser.email, assignCustomerId, assignRole);
      const customer = customers.find(c => c.id === assignCustomerId);
      toast.success(`${selectedUser.email} assigned to ${customer?.name || 'customer'} as ${assignRole}`);
      setAssignDialogOpen(false);
      // Refresh user in list
      setUsers(users.map(u => 
        u.id === selectedUser.id 
          ? { ...u, role: assignRole, customer_id: assignCustomerId }
          : u
      ));
      setSelectedUser(null);
      setAssignCustomerId('');
      setAssignRole('admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setLoading(false);
    }
  };

  // Reset Password
  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    try {
      await api.adminResetUserPassword(selectedUser.id, newPassword);
      toast.success(`Password reset for ${selectedUser.email}`);
      setResetPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Password reset failed');
    } finally {
      setLoading(false);
    }
  };

  // Get customer name
  const getCustomerName = (customerId) => {
    if (!customerId) return 'Platform (No Customer)';
    const customer = customers.find(c => c.id === customerId);
    return customer?.name || 'Unknown';
  };

  // Get role badge color
  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return <Badge className="bg-purple-600">Super Admin</Badge>;
      case 'admin':
        return <Badge className="bg-blue-600">Admin</Badge>;
      default:
        return <Badge variant="secondary">User</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="w-5 h-5" />
          User Management
        </CardTitle>
        <CardDescription>
          Search, manage, and modify user accounts across all customers
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Search Bar */}
        <div className="flex gap-2 mb-4">
          <Input
            placeholder="Search by email or name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1"
            data-testid="user-search-input"
          />
          <Button onClick={handleSearch} disabled={loading} data-testid="user-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span className="ml-2">Search</span>
          </Button>
          <Button variant="outline" onClick={loadAllUsers} disabled={loading} data-testid="load-all-users-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            <span className="ml-2">Load All</span>
          </Button>
        </div>

        {/* Results */}
        {users.length > 0 && (
          <div className="border rounded-lg divide-y max-h-96 overflow-y-auto" data-testid="user-results">
            {users.map((user) => (
              <div key={user.id} className="p-4 hover:bg-muted/50">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{user.name || user.full_name || 'No name'}</p>
                      {getRoleBadge(user.role)}
                      {!user.is_active && <Badge variant="destructive">Suspended</Badge>}
                    </div>
                    <p className="text-sm text-muted-foreground">{user.email}</p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Building className="w-3 h-3" />
                      {getCustomerName(user.customer_id)}
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(user);
                        setResetPasswordDialogOpen(true);
                      }}
                      data-testid={`reset-pwd-${user.id}`}
                    >
                      <Key className="w-3 h-3 mr-1" />
                      Password
                    </Button>
                    
                    {user.role !== 'super_admin' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedUser(user);
                          setPromoteDialogOpen(true);
                        }}
                        data-testid={`promote-${user.id}`}
                      >
                        <Shield className="w-3 h-3 mr-1" />
                        Promote
                      </Button>
                    )}
                    
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedUser(user);
                        setAssignCustomerId(user.customer_id || '');
                        setAssignRole(user.role === 'super_admin' ? 'admin' : user.role);
                        setAssignDialogOpen(true);
                      }}
                      data-testid={`assign-${user.id}`}
                    >
                      <UserCog className="w-3 h-3 mr-1" />
                      Assign
                    </Button>
                    
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setSelectedUser(user);
                        setDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-${user.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {users.length === 0 && !loading && (
          <p className="text-center text-muted-foreground py-8">
            Search for users or click &quot;Load All&quot; to see all users
          </p>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.email}</strong>? 
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Delete User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Promote Dialog */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promote to Super Admin</DialogTitle>
            <DialogDescription>
              Promote <strong>{selectedUser?.email}</strong> to Super Admin? 
              They will have full platform access and their customer association will be removed.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPromoteDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handlePromote} disabled={loading}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Promote to Super Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign to Customer Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign User to Customer</DialogTitle>
            <DialogDescription>
              Assign <strong>{selectedUser?.email}</strong> to a customer organization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Customer</Label>
              <Select value={assignCustomerId} onValueChange={setAssignCustomerId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a customer" />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={assignRole} onValueChange={setAssignRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssign} disabled={loading || !assignCustomerId}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Assign User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordDialogOpen} onOpenChange={setResetPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{selectedUser?.email}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password (min 6 characters)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setResetPasswordDialogOpen(false);
              setNewPassword('');
            }}>
              Cancel
            </Button>
            <Button onClick={handleResetPassword} disabled={loading || !newPassword}>
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
